import PropTypes from 'prop-types';
import { useState, useEffect, useMemo } from 'react';
import { useI18n } from '../../../../context/I18nContext';
import { getAttachmentIcon } from '../../../../utils/fileUtils';
import './SendAttachmentModal.css';

const SendAttachmentModal = ({
  thumbnail = null,
  fileName = '',
  mimeType = '',
  fileType = 'm.file',
  onSendClick = (_caption) => {},
  onCancelClick = () => {},
  isUploading = false,
  onCancelUpload = () => {},
  isGeneratingThumbnail = false,
  uploadProgress = -1, // -1 = not started, 0 = preparing, 1-100 = uploading
}) => {
  const { t } = useI18n();

  const [caption, setCaption] = useState('');

  // Determine if upload is in progress
  const isUploadInProgress = uploadProgress >= 0;

  const handleSendAttachment = async () => {
    if (isUploading) return; // Prevent multiple clicks
    
    try {
      await onSendClick(caption.trim());
    } catch {
      // Error is already handled by the parent component
    }
  };

  const handleCancelClick = () => {
    if (isUploading) {
      // If upload is in progress, cancel the upload
      onCancelUpload();
    } else {
      // If just previewing, close the modal
      onCancelClick();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendAttachment();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelClick();
    }
  };

  // Generate thumbnail URL if available, otherwise get file type icon
  const displayImageSrc = useMemo(() => {
    return thumbnail 
      ? URL.createObjectURL(thumbnail)
      : getAttachmentIcon(fileType, fileName, mimeType);
  }, [thumbnail, fileType, fileName, mimeType]);

  const isActualThumbnail = Boolean(thumbnail);

  // Clean up object URL when component unmounts or thumbnail changes
  useEffect(() => {
    if (thumbnail && displayImageSrc.startsWith('blob:')) {
      return () => {
        URL.revokeObjectURL(displayImageSrc);
      };
    }
  }, [thumbnail, displayImageSrc]);

  return (
    <div className="send-attachment-modal">
      <div className="send-attachment-modal-preview">
        {isGeneratingThumbnail ? (
          <div className="send-attachment-modal-image placeholder" />
        ) : (
          <img
            src={displayImageSrc}
            alt={isActualThumbnail ? 'Attachment preview' : 'File type icon'}
            className={`send-attachment-modal-image ${isActualThumbnail ? 'thumbnail' : 'file-icon'}`}
          />
        )}
        <span className="send-attachment-modal-filename">{fileName}</span>
        {/* Progress bar at bottom */}
        {isUploadInProgress && (
          <div className="send-attachment-modal-progress-bar">
            <div 
              className="send-attachment-modal-progress-fill"
              style={{ width: `${uploadProgress}%` }}
            />
            <div className="send-attachment-modal-progress-content">
              <span className="material-icons spin">autorenew</span>
              <span className="send-attachment-modal-progress-text">
                {uploadProgress}%
              </span>
            </div>
          </div>
        )}
      </div>
      
      {/* Hide caption during upload */}
      {!isUploadInProgress && (
        <textarea
          className="send-attachment-modal-caption"
          placeholder={t('AttachmentPreviewModal.addCaptionPlaceholder')}
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      )}
      
      <button
        className="send-attachment-modal-send"
        onClick={handleSendAttachment}
        disabled={isUploadInProgress || isGeneratingThumbnail}
        type="button"
        style={{ display: isUploadInProgress ? 'none' : 'block' }}
      >
        {t('send')}
      </button>
      <div className="send-attachment-modal-close" onClick={handleCancelClick}>
        <span className="material-icons">close</span>
      </div>
    </div>
  );
};

SendAttachmentModal.propTypes = {
  thumbnail: PropTypes.instanceOf(Blob),
  fileName: PropTypes.string,
  mimeType: PropTypes.string,
  fileType: PropTypes.string,
  onSendClick: PropTypes.func,
  onCancelClick: PropTypes.func,
  isUploading: PropTypes.bool,
  onCancelUpload: PropTypes.func,
  isGeneratingThumbnail: PropTypes.bool,
  uploadProgress: PropTypes.number,
};

export default SendAttachmentModal;
